import { useMemo, useState } from "react";
import {
  checkInVisitor,
  checkoutVisitor,
  denyVisitor,
  enterVisitor,
  enterWithPass,
  listHostsDirectory,
  lookupVisitByBadge,
  scanVisitorId,
  setVisitorIdNumber,
  uploadVisitorIdDocument,
  uploadVisitorPhoto,
} from "./api";
import { getActiveGatePassTemplate, getRequiredVisitorFields } from "../admin/api";
import { useAdminResource } from "../admin/use-admin-resource";
import { missingRequiredLabels } from "../../lib/visitor-fields";
import { DashboardCustomizer } from "./DashboardCustomizer";
import { DashboardShell } from "./DashboardShell";
import { SiteSwitcher } from "./SiteSwitcher";
import { useSiteContext } from "./use-site-context";
import { formatDateTime } from "./format";
import { isValidIndianPhone } from "../../lib/validation";
import { fileTooLargeError } from "../../lib/upload";
import type { VisitRow } from "./types";
import { useDashboardData } from "./use-dashboard-data";
import { useWidgetPrefs } from "./useWidgetPrefs";
import { EmptyRow, RecentVisitorList } from "./ui";
import { VisitsSparkline } from "./VisitsSparkline";
import { GATE_DEFAULT_WIDGETS, GATE_WIDGETS } from "./widgets";
import { FilePicker } from "../../components/FilePicker";
import { QrBadge } from "../../components/QrBadge";
import { QrScanner } from "../../components/QrScanner";
import { useToast } from "../../components/useToast";

function visitOptionLabel(visit: VisitRow): string {
  return visit.company ? `${visit.visitor_name} -- ${visit.company}` : visit.visitor_name;
}

type GateAction = "checkin" | "enter" | "checkout" | "deny";

type GateCheckInForm = {
  fullName: string;
  hostId: string;
  purpose: string;
  phone: string;
  email: string;
  company: string;
  expectedExit: string;
  notes: string;
  hasLaptop: boolean;
  laptopMac: string;
  laptopSerial: string;
  idType: string;
  idNumber: string;
  siteId: string; // "" = let the server pick (host's site / default)
};

const INITIAL_CHECKIN_FORM: GateCheckInForm = {
  fullName: "",
  hostId: "",
  purpose: "",
  phone: "",
  email: "",
  company: "",
  expectedExit: "",
  notes: "",
  hasLaptop: false,
  laptopMac: "",
  laptopSerial: "",
  idType: "AADHAAR",
  idNumber: "",
  siteId: "",
};

function GateTodayTable({ rows }: { rows: VisitRow[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Visitor</th>
            <th>Status</th>
            <th>Check-in</th>
            <th>Check-out</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow label="No gate activity today" colSpan={4} />
          ) : (
            rows.map((visit) => (
              <tr key={`gate-today-${visit.visit_id}`}>
                <td>{visit.visitor_name}</td>
                <td>{visit.status ?? "-"}</td>
                <td>{formatDateTime(visit.checkin_time)}</td>
                <td>{formatDateTime(visit.checkout_time)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function parsePositiveInt(rawValue: string): number | null {
  const value = Number(rawValue);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}

export function GateDashboardPage() {
  const siteContext = useSiteContext();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const { data, loading, refreshing, error, lastUpdated, refresh } = useDashboardData(selectedSiteId);
  const { showSuccess, showError } = useToast();
  const { widgetIds, saving, save, resetToDefault } = useWidgetPrefs(GATE_DEFAULT_WIDGETS);
  const [showCustomizer, setShowCustomizer] = useState(false);

  const todayVisits = useMemo(() => data?.todayVisits ?? [], [data]);
  const recentVisitors = useMemo(() => data?.recentVisitors ?? [], [data]);
  const alerts = data?.alerts;
  const stats = data?.stats;

  const { data: hosts } = useAdminResource(() => listHostsDirectory(), []);
  const { data: requiredFieldsData } = useAdminResource(() => getRequiredVisitorFields(), []);
  const requiredFields = requiredFieldsData?.required_fields ?? [];
  const { data: gatePassTemplate } = useAdminResource(() => getActiveGatePassTemplate(), []);
  // Not todayVisits.filter(status === "APPROVED") -- /dashboard/today only
  // returns visits with a checkin_time set (INSIDE/OVERSTAY/CHECKED_OUT),
  // which by definition excludes APPROVED (checkin_time is null until Enter
  // actually happens). That filter was always empty, so this dropdown never
  // showed anyone awaiting entry -- Scan Badge was the only working path.
  const approvedVisits = useMemo(() => data?.approvedVisits ?? [], [data]);
  const insideVisits = useMemo(() => data?.summary.inside ?? [], [data]);
  // Same bug as approvedVisits above -- todayVisits never contains PENDING
  // visits (no checkin_time yet), so this must come from pendingApprovals
  // instead, not be filtered out of todayVisits.
  const pendingVisits = useMemo(() => data?.pendingApprovals ?? [], [data]);

  const [checkInForm, setCheckInForm] = useState<GateCheckInForm>(INITIAL_CHECKIN_FORM);
  const [checkInPhoto, setCheckInPhoto] = useState<File | null>(null);
  const [checkInIdDocument, setCheckInIdDocument] = useState<File | null>(null);
  // ID verification at the gate. idVerified: null = no ID scanned yet, true =
  // scan passed local validation, false = scan failed / captured-but-unverified.
  const [idScanning, setIdScanning] = useState(false);
  const [idScanMsg, setIdScanMsg] = useState<string | null>(null);
  const [idVerified, setIdVerified] = useState<boolean | null>(null);
  const [entryDecision, setEntryDecision] = useState<"reverify" | "allow_in">("reverify");
  const [enterId, setEnterId] = useState("");
  const [checkoutId, setCheckoutId] = useState("");
  const [denyId, setDenyId] = useState("");
  const [pendingAction, setPendingAction] = useState<GateAction | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastBadge, setLastBadge] = useState<string | null>(null);

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerTarget, setScannerTarget] = useState<"enter" | "checkout" | null>(null);

  // Capture the visitor's ID at the gate: the same file becomes the ID-document
  // upload AND is OCR'd to auto-fill the number. A valid scan marks the visit
  // verified; a failed scan drops to manual entry + the re-verify/allow-in choice.
  async function handleIdScan(file: File | null) {
    setCheckInIdDocument(file);
    if (!file) {
      setIdVerified(null);
      setIdScanMsg(null);
      return;
    }
    const sizeError = fileTooLargeError(file, "ID document");
    if (sizeError) {
      setActionError(sizeError);
      return;
    }
    setIdScanning(true);
    setIdScanMsg("Scanning ID…");
    try {
      const res = await scanVisitorId(file);
      setIdScanMsg(res.validation_message);
      setIdVerified(res.valid);
      setEntryDecision("reverify");
      setCheckInForm((prev) => ({
        ...prev,
        idNumber: res.id_number ?? prev.idNumber,
        idType: res.id_type ?? prev.idType,
        fullName: prev.fullName.trim() ? prev.fullName : res.full_name ?? prev.fullName,
      }));
    } catch {
      setIdScanMsg("Couldn't scan the ID automatically — enter the number manually.");
      setIdVerified(false);
    } finally {
      setIdScanning(false);
    }
  }

  async function runCheckIn() {
    const hostId = parsePositiveInt(checkInForm.hostId);
    if (!checkInForm.fullName.trim()) {
      setActionMessage(null);
      setActionError("Full name is required for check-in");
      return;
    }
    if (!hostId) {
      setActionMessage(null);
      setActionError("Select a host for this visit");
      return;
    }
    if (!checkInForm.purpose.trim()) {
      setActionMessage(null);
      setActionError("Purpose is required for check-in");
      return;
    }

    if (checkInForm.expectedExit.trim()) {
      const expected = new Date(checkInForm.expectedExit);
      if (Number.isNaN(expected.getTime())) {
        setActionMessage(null);
        setActionError("Expected exit must be a valid date and time");
        return;
      }
    }

    // Enforce the admin's mandatory-field config (including files/ID number,
    // which the server can't check because they're follow-up uploads).
    const missing = missingRequiredLabels(requiredFields, {
      phone: !!checkInForm.phone.trim(),
      email: !!checkInForm.email.trim(),
      company: !!checkInForm.company.trim(),
      expected_exit: !!checkInForm.expectedExit.trim(),
      notes: !!checkInForm.notes.trim(),
      photo: !!checkInPhoto,
      id_document: !!checkInIdDocument,
      id_number: !!checkInForm.idNumber.trim(),
      laptop: checkInForm.hasLaptop && !!checkInForm.laptopMac.trim() && !!checkInForm.laptopSerial.trim(),
    });
    if (missing.length > 0) {
      setActionMessage(null);
      setActionError(`Required: ${missing.join(", ")}`);
      return;
    }

    // Catch oversized photo/ID uploads up front -- otherwise the visitor gets
    // checked in but the file upload fails afterward, which reads as a broken
    // submit even though the visit was created.
    const sizeError =
      fileTooLargeError(checkInPhoto, "Photo") ?? fileTooLargeError(checkInIdDocument, "ID document");
    if (sizeError) {
      setActionMessage(null);
      setActionError(sizeError);
      return;
    }

    setPendingAction("checkin");
    setActionMessage(null);
    setActionError(null);
    setLastBadge(null);

    try {
      const result = await checkInVisitor({
        full_name: checkInForm.fullName.trim(),
        host_id: hostId,
        purpose: checkInForm.purpose.trim(),
        phone: checkInForm.phone.trim() || undefined,
        email: checkInForm.email.trim() || undefined,
        company: checkInForm.company.trim() || undefined,
        expected_exit: checkInForm.expectedExit.trim() || undefined,
        notes: checkInForm.notes.trim() || undefined,
        has_laptop: checkInForm.hasLaptop,
        laptop_mac_address: checkInForm.hasLaptop ? checkInForm.laptopMac.trim() || undefined : undefined,
        laptop_serial_number: checkInForm.hasLaptop ? checkInForm.laptopSerial.trim() || undefined : undefined,
        // Ignored server-side for a site-scoped operator; used by a
        // company-wide operator to place the walk-in at a chosen site.
        site_id: checkInForm.siteId ? Number(checkInForm.siteId) : null,
        id_verified: idVerified,
        // Only meaningful when the ID couldn't be verified.
        entry_decision: idVerified === false ? entryDecision : undefined,
      });

      const message = `Checked in ${checkInForm.fullName.trim()} as visit ${result.visit_id} (${result.badge_code})`;
      setActionMessage(message);
      showSuccess(message);
      setLastBadge(result.badge_code);
      setEnterId(String(result.visit_id));

      // Photo/ID capture happens as follow-up calls against the visitor
      // just created, not as part of check-in itself -- the check-in has
      // already succeeded by this point, so a failure here is a warning,
      // not something that should make the guard think the visitor wasn't
      // actually checked in.
      const captureFailures: string[] = [];
      if (checkInPhoto) {
        try {
          await uploadVisitorPhoto(result.visitor_id, checkInPhoto);
        } catch {
          captureFailures.push("photo");
        }
      }
      if (checkInIdDocument) {
        try {
          await uploadVisitorIdDocument(result.visitor_id, checkInIdDocument);
        } catch {
          captureFailures.push("ID document");
        }
      }
      if (checkInForm.idNumber.trim()) {
        try {
          await setVisitorIdNumber(result.visitor_id, checkInForm.idType, checkInForm.idNumber.trim());
        } catch {
          captureFailures.push("ID number");
        }
      }
      if (captureFailures.length > 0) {
        showError(`Checked in, but failed to save: ${captureFailures.join(", ")}`);
      }

      setCheckInForm(INITIAL_CHECKIN_FORM);
      setCheckInPhoto(null);
      setCheckInIdDocument(null);
      setIdVerified(null);
      setIdScanMsg(null);
      setEntryDecision("reverify");
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Check-in failed at gate";
      setActionError(message);
      showError(message);
    } finally {
      setPendingAction(null);
    }
  }

  async function runVisitTransition(type: "enter" | "checkout" | "deny", rawId: string) {
    const visitId = parsePositiveInt(rawId);
    if (!visitId) {
      setActionMessage(null);
      setActionError("Select a visitor first");
      return;
    }

    setPendingAction(type);
    setActionMessage(null);
    setActionError(null);

    try {
      const result =
        type === "enter"
          ? await enterVisitor(visitId)
          : type === "checkout"
            ? await checkoutVisitor(visitId)
            : await denyVisitor(visitId);

      const message = `Visit ${result.visit_id} moved to ${result.status}`;
      setActionMessage(message);
      showSuccess(message);
      if (type === "enter") setEnterId("");
      if (type === "checkout") setCheckoutId("");
      if (type === "deny") setDenyId("");
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Action failed at gate";
      setActionError(message);
      showError(message);
    } finally {
      setPendingAction(null);
    }
  }

  function openScanner(target: "enter" | "checkout") {
    setScannerTarget(target);
    setScannerOpen(true);
  }

  async function handleScanned(badgeCode: string) {
    setScannerOpen(false);
    const target = scannerTarget;
    setScannerTarget(null);
    if (!target) return;

    // Scanning a badge at the gate IS the deliberate "process this person
    // now" action -- previously this only populated the Enter/Checkout
    // dropdown and left it to the guard to notice and press a second
    // button. That silent extra step is exactly what let a scanned visitor
    // (krishna prasad, visit #87) sit at APPROVED for 6+ minutes with no
    // indication anything was still pending -- security believed the scan
    // itself completed entry. Submit the transition immediately instead.
    try {
      const visit = await lookupVisitByBadge(badgeCode);
      if (target === "enter") {
        if (visit.gate_reverify_required) {
          // Security flagged this ID for a manual re-check at the gate.
          showError(`⚠ ID not verified for ${visit.visitor_name} — re-verify before letting them in.`);
        }
        setEnterId(String(visit.visit_id));
        await runVisitTransition("enter", String(visit.visit_id));
      } else {
        setCheckoutId(String(visit.visit_id));
        await runVisitTransition("checkout", String(visit.visit_id));
      }
    } catch (requestError) {
      // Not a single-visit badge -- it may be a reusable long-term pass, which
      // has no Visit row until it's scanned. Fall back to the pass-entry path.
      if (target === "enter") {
        try {
          const entry = await enterWithPass(badgeCode);
          const action = entry.status === "CHECKED_OUT" ? "checked out" : "entered";
          const message = `Reusable pass — ${entry.visitor_name} ${action} (visit ${entry.visit_id})`;
          setActionMessage(message);
          showSuccess(message);
          await refresh();
          return;
        } catch (passError) {
          showError(passError instanceof Error ? passError.message : "Badge not recognized");
          return;
        }
      }
      showError(requestError instanceof Error ? requestError.message : "Badge not recognized");
    }
  }

  return (
    <DashboardShell
      roleName="Gate"
      title="Gate Operations"
      subtitle="Handle visitor registration, entry, exit, and denial from one control panel."
      loading={loading}
      refreshing={refreshing}
      error={error}
      lastUpdated={lastUpdated}
      onRefresh={refresh}
      siteSwitcher={
        <SiteSwitcher context={siteContext} value={selectedSiteId} onChange={setSelectedSiteId} />
      }
      showCustomizer={showCustomizer}
      onToggleCustomize={() => setShowCustomizer((prev) => !prev)}
      customizerPanel={
        <DashboardCustomizer
          catalog={GATE_WIDGETS}
          value={widgetIds}
          saving={saving}
          onSave={async (ids) => {
            await save(ids);
            setShowCustomizer(false);
          }}
          onResetToDefault={async () => {
            await resetToDefault();
            setShowCustomizer(false);
          }}
          onClose={() => setShowCustomizer(false)}
        />
      }
    >
      {scannerOpen ? (
        <QrScanner onDetected={(text) => void handleScanned(text)} onClose={() => setScannerOpen(false)} />
      ) : null}

      <section className="stats-grid fade-in delay-1">
        <article className="stat-card">
          <p className="stat-label">Today Visits</p>
          <p className="stat-value">{stats?.today_visits ?? 0}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Inside</p>
          <p className="stat-value">{stats?.inside_visitors ?? 0}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Overstay</p>
          <p className="stat-value">{stats?.overstay_visitors ?? 0}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Pending Queue</p>
          <p className="stat-value">{alerts?.pending_approval_count ?? 0}</p>
        </article>
        <VisitsSparkline />
      </section>

      <section className="panel fade-in delay-2">
        <header className="panel-header">
          <h2>Quick Actions</h2>
          <span>Check-in / Enter / Check-out / Deny</span>
        </header>
        <div className="action-grid gate-action-grid">
          <form
            className="action-form action-card"
            onSubmit={(event) => {
              event.preventDefault();
              void runCheckIn();
            }}
          >
            <label htmlFor="gate-checkin-full-name">Check-In Visitor</label>
            <div className="checkin-fields">
              <input
                id="gate-checkin-full-name"
                value={checkInForm.fullName}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, fullName: event.target.value }))
                }
                placeholder="Full name *"
              />
              <select
                className="search-input"
                value={checkInForm.hostId}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, hostId: event.target.value }))
                }
                aria-label="Host"
              >
                <option value="">{hosts?.length ? "Select host *" : "Loading hosts..."}</option>
                {(hosts ?? []).map((host) => (
                  <option key={host.id} value={host.id}>
                    {host.name}
                  </option>
                ))}
              </select>
              {siteContext && !siteContext.scoped && siteContext.sites.length > 0 ? (
                <select
                  className="search-input"
                  value={checkInForm.siteId}
                  onChange={(event) =>
                    setCheckInForm((prev) => ({ ...prev, siteId: event.target.value }))
                  }
                  aria-label="Site"
                >
                  <option value="">Site: auto (host's site)</option>
                  {siteContext.sites.map((site) => (
                    <option key={site.id} value={String(site.id)}>
                      {site.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                value={checkInForm.purpose}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, purpose: event.target.value }))
                }
                placeholder="Purpose *"
              />
              <div className="field-with-hint">
                <input
                  value={checkInForm.phone}
                  onChange={(event) =>
                    setCheckInForm((prev) => ({ ...prev, phone: event.target.value }))
                  }
                  placeholder="Phone (10-digit mobile)"
                  inputMode="numeric"
                />
                {checkInForm.phone.trim() && !isValidIndianPhone(checkInForm.phone) ? (
                  <span className="field-hint">Enter a valid 10-digit mobile number</span>
                ) : null}
              </div>
              <input
                type="email"
                value={checkInForm.email}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, email: event.target.value }))
                }
                placeholder="Email"
                aria-label="Visitor email"
              />
              <input
                value={checkInForm.company}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, company: event.target.value }))
                }
                placeholder="Company"
              />
              <input
                type="datetime-local"
                value={checkInForm.expectedExit}
                onChange={(event) =>
                  setCheckInForm((prev) => ({ ...prev, expectedExit: event.target.value }))
                }
                placeholder="Expected exit"
              />
              <input
                value={checkInForm.notes}
                onChange={(event) => setCheckInForm((prev) => ({ ...prev, notes: event.target.value }))}
                placeholder="Notes / description"
              />
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={checkInForm.hasLaptop}
                  onChange={(event) =>
                    setCheckInForm((prev) => ({ ...prev, hasLaptop: event.target.checked }))
                  }
                />
                Bringing a laptop
              </label>
              {checkInForm.hasLaptop ? (
                <>
                  <input
                    value={checkInForm.laptopMac}
                    onChange={(event) =>
                      setCheckInForm((prev) => ({ ...prev, laptopMac: event.target.value }))
                    }
                    placeholder="Laptop MAC address"
                  />
                  <input
                    value={checkInForm.laptopSerial}
                    onChange={(event) =>
                      setCheckInForm((prev) => ({ ...prev, laptopSerial: event.target.value }))
                    }
                    placeholder="Laptop serial / system number"
                  />
                </>
              ) : null}
              <select
                className="search-input"
                value={checkInForm.idType}
                onChange={(event) => setCheckInForm((prev) => ({ ...prev, idType: event.target.value }))}
                aria-label="ID type"
              >
                <option value="AADHAAR">Aadhaar</option>
                <option value="PAN">PAN</option>
                <option value="DRIVING_LICENSE">Driving licence</option>
                <option value="PASSPORT">Passport</option>
                <option value="OTHER">Other ID</option>
              </select>
              <input
                value={checkInForm.idNumber}
                onChange={(event) => {
                  const idNumber = event.target.value;
                  // Typing over an auto-filled number means the operator is
                  // entering it manually -- treat as captured-but-unverified.
                  setCheckInForm((prev) => ({ ...prev, idNumber }));
                  if (idVerified !== false) setIdVerified(idNumber.trim() ? false : null);
                }}
                placeholder="ID number (scan or type)"
              />
              <FilePicker label="Visitor photo (optional)" file={checkInPhoto} onChange={setCheckInPhoto} />
              <FilePicker
                label={idScanning ? "Scanning ID…" : "Scan / capture ID (auto-fills number)"}
                file={checkInIdDocument}
                onChange={(file) => void handleIdScan(file)}
              />
            </div>
            <p className="id-scan-hint">
              💡 Hold the ID flat and fill the frame — avoid glare and shadows for the most accurate read.
            </p>

            {idScanMsg || idVerified !== null ? (
              <div className={`id-verify-banner ${idVerified ? "id-verify-ok" : "id-verify-warn"}`}>
                <strong>
                  {idVerified === true
                    ? "ID verified"
                    : idVerified === false
                      ? "ID not verified"
                      : "ID"}
                </strong>
                {idScanMsg ? <span> — {idScanMsg}</span> : null}
              </div>
            ) : null}

            {idVerified === false ? (
              <fieldset className="id-decision">
                <legend>ID couldn't be verified — how should the gate handle it?</legend>
                <label className="radio-field">
                  <input
                    type="radio"
                    name="entry-decision"
                    checked={entryDecision === "reverify"}
                    onChange={() => setEntryDecision("reverify")}
                  />
                  Re-verify at the gate (flag this visit for a manual re-check)
                </label>
                <label className="radio-field">
                  <input
                    type="radio"
                    name="entry-decision"
                    checked={entryDecision === "allow_in"}
                    onChange={() => setEntryDecision("allow_in")}
                  />
                  Allow direct in (override — recorded in the audit log)
                </label>
              </fieldset>
            ) : null}
            <button className="refresh-btn action-submit" type="submit" disabled={pendingAction === "checkin"}>
              {pendingAction === "checkin" ? "Checking in..." : "Check In"}
            </button>
          </form>

          <div className="gate-action-secondary">
            <form
              className="action-form action-card"
              onSubmit={(event) => {
                event.preventDefault();
                void runVisitTransition("enter", enterId);
              }}
            >
              <label htmlFor="gate-enter-id">Enter Visitor (APPROVED -&gt; INSIDE)</label>
              <div className="action-row">
                <select
                  id="gate-enter-id"
                  className="search-input"
                  value={enterId}
                  onChange={(event) => setEnterId(event.target.value)}
                >
                  <option value="">
                    {approvedVisits.length ? "Select visitor *" : "No visitors awaiting entry"}
                  </option>
                  {approvedVisits.map((visit) => (
                    <option key={visit.visit_id} value={visit.visit_id}>
                      {visitOptionLabel(visit)}
                    </option>
                  ))}
                </select>
                <button className="refresh-btn" type="submit" disabled={pendingAction === "enter"}>
                  {pendingAction === "enter" ? "Submitting..." : "Enter"}
                </button>
                <button type="button" className="secondary-btn" onClick={() => openScanner("enter")}>
                  Scan Badge
                </button>
              </div>
            </form>

            <form
              className="action-form action-card"
              onSubmit={(event) => {
                event.preventDefault();
                void runVisitTransition("checkout", checkoutId);
              }}
            >
              <label htmlFor="gate-checkout-id">Check Out Visitor (INSIDE -&gt; CHECKED_OUT)</label>
              <div className="action-row">
                <select
                  id="gate-checkout-id"
                  className="search-input"
                  value={checkoutId}
                  onChange={(event) => setCheckoutId(event.target.value)}
                >
                  <option value="">
                    {insideVisits.length ? "Select visitor *" : "No visitors currently inside"}
                  </option>
                  {insideVisits.map((visit) => (
                    <option key={visit.visit_id} value={visit.visit_id}>
                      {visitOptionLabel(visit)}
                    </option>
                  ))}
                </select>
                <button className="refresh-btn" type="submit" disabled={pendingAction === "checkout"}>
                  {pendingAction === "checkout" ? "Submitting..." : "Check Out"}
                </button>
                <button type="button" className="secondary-btn" onClick={() => openScanner("checkout")}>
                  Scan Badge
                </button>
              </div>
            </form>

            <form
              className="action-form action-card"
              onSubmit={(event) => {
                event.preventDefault();
                void runVisitTransition("deny", denyId);
              }}
            >
              <label htmlFor="gate-deny-id">Deny Visitor (PENDING -&gt; REJECTED)</label>
              <div className="action-row">
                <select
                  id="gate-deny-id"
                  className="search-input"
                  value={denyId}
                  onChange={(event) => setDenyId(event.target.value)}
                >
                  <option value="">
                    {pendingVisits.length ? "Select visitor *" : "No visitors pending approval"}
                  </option>
                  {pendingVisits.map((visit) => (
                    <option key={visit.visit_id} value={visit.visit_id}>
                      {visitOptionLabel(visit)}
                    </option>
                  ))}
                </select>
                <button className="refresh-btn danger-action-btn" type="submit" disabled={pendingAction === "deny"}>
                  {pendingAction === "deny" ? "Submitting..." : "Deny"}
                </button>
              </div>
            </form>
          </div>
        </div>
        {actionMessage ? <p className="success-banner">{actionMessage}</p> : null}
        {actionError ? <p className="error-banner action-error">{actionError}</p> : null}
        {lastBadge ? (
          <div className="tool-card">
            <p>Badge for the visitor just checked in:</p>
            <QrBadge badgeCode={lastBadge} template={gatePassTemplate} />
          </div>
        ) : null}
      </section>

      <section className="panel-grid fade-in delay-3">
        {widgetIds.map((widgetId) => {
          if (widgetId === "today-activity") {
            return (
              <article className="panel" key="today-activity">
                <header className="panel-header">
                  <h2>Today Activity</h2>
                  <span>{todayVisits.length} rows</span>
                </header>
                <GateTodayTable rows={todayVisits} />
              </article>
            );
          }
          if (widgetId === "recent-visitors") {
            return (
              <article className="panel" key="recent-visitors">
                <header className="panel-header">
                  <h2>Recent Visitors</h2>
                  <span>{recentVisitors.length} rows</span>
                </header>
                <ul className="list-stack">
                  <RecentVisitorList rows={recentVisitors} />
                </ul>
              </article>
            );
          }
          return null;
        })}
      </section>
    </DashboardShell>
  );
}
